import { PropsWithChildren, useMemo } from 'react'
import dayjs from 'dayjs'
import { ColumnDef } from '@tanstack/react-table'
import { DataTable } from '@/components/ui/data-table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  getRolesWithMultipleSpecs,
  getSpecializationsByClass,
} from 'app/frontend/base/services/characterValues'
import {
  ClassDisplay,
  RoleDisplay,
} from 'app/frontend/base/pages/TeamDashboard/subpages/TeamRoster/components/EditableRosterCells'
import { MainSpecMenuItems } from 'app/frontend/base/pages/TeamDashboard/subpages/TeamRoster/components/TeamMemberEditMenus'
import CandidateName from './CandidateName'
import CandidateCommentAction from './CandidateCommentAction'
import CandidateTeamSelect from './CandidateTeamSelect'
import CandidateContextMenu from './CandidateContextMenu'
import CandidateUserBadge from './CandidateUserBadge'
import {
  useIsCandidateAdmin,
  useIsOwnCandidate,
  useUpdateCandidate,
} from '../hooks'

type Props = {
  candidates: wowaudit.GuildCandidate[]
}

const useCanEditCandidate = (candidate: wowaudit.GuildCandidate) => {
  const isAdmin = useIsCandidateAdmin()
  const isOwn = useIsOwnCandidate(candidate)
  return isAdmin || isOwn
}

const SpecMenu = ({
  candidate,
  children,
}: PropsWithChildren<{ candidate: wowaudit.GuildCandidate }>) => {
  const canEdit = useCanEditCandidate(candidate)
  const { mutate } = useUpdateCandidate()

  if (!canEdit) return <>{children}</>

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent onCloseAutoFocus={(e) => e.preventDefault()}>
        <MainSpecMenuItems
          member={{ role: candidate.role, specId: candidate.specId }}
          classSpecializations={getSpecializationsByClass(
            candidate.characterReference.classId,
          )}
          rolesWithMultipleSpecs={getRolesWithMultipleSpecs(
            candidate.characterReference.classId,
          )}
          onRoleChange={(role) =>
            (role !== candidate.role || candidate.specId !== null) &&
            mutate({
              characterId: candidate.characterReference.id,
              role,
              specId: null,
            })
          }
          onSpecChange={(specId) =>
            specId !== candidate.specId &&
            mutate({ characterId: candidate.characterReference.id, specId })
          }
        />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

const RoleCell = ({ candidate }: { candidate: wowaudit.GuildCandidate }) => {
  const canEdit = useCanEditCandidate(candidate)

  return (
    <SpecMenu candidate={candidate}>
      <RoleDisplay role={candidate.role} clickable={canEdit} />
    </SpecMenu>
  )
}

const ClassCell = ({ candidate }: { candidate: wowaudit.GuildCandidate }) => {
  const canEdit = useCanEditCandidate(candidate)

  return (
    <SpecMenu candidate={candidate}>
      <ClassDisplay
        characterClass={candidate.characterReference.characterClass}
        specDetails={candidate.specDetails}
        clickable={canEdit}
      />
    </SpecMenu>
  )
}

const CandidateRowMenu = ({
  candidate,
}: {
  candidate: wowaudit.GuildCandidate
}) => {
  const isAdmin = useIsCandidateAdmin()
  const isOwn = useIsOwnCandidate(candidate)

  if (!isAdmin && !isOwn) return null

  return <CandidateContextMenu candidate={candidate} size='medium' />
}

const CandidateTable = ({ candidates }: Props) => {
  const isAdmin = useIsCandidateAdmin()

  const columns: ColumnDef<wowaudit.GuildCandidate>[] = useMemo(
    () => [
      {
        id: 'name',
        header: 'Name',
        accessorFn: (candidate) => candidate.characterReference.name,
        cell: ({ row }) => <CandidateName candidate={row.original} />,
      },
      {
        id: 'user',
        header: 'User',
        accessorFn: (candidate) => candidate.userBattletag ?? '',
        cell: ({ row }) => (
          <CandidateUserBadge
            candidate={row.original}
            className='border rounded-lg w-fit'
          />
        ),
      },
      {
        id: 'role',
        header: 'Role',
        accessorFn: (candidate) => candidate.role,
        cell: ({ row }) => <RoleCell candidate={row.original} />,
      },
      {
        id: 'characterClass',
        header: 'Class',
        accessorFn: (candidate) => candidate.characterReference.characterClass,
        cell: ({ row }) => <ClassCell candidate={row.original} />,
      },
      {
        id: 'comment',
        header: 'Comment',
        enableSorting: false,
        cell: ({ row }) => (
          <div className='flex items-center gap-1 max-w-64'>
            <CandidateCommentAction candidate={row.original} />
            <span className='truncate text-muted-foreground'>
              {row.original.comment}
            </span>
          </div>
        ),
      },
      {
        id: 'createdAt',
        header: 'Added',
        accessorFn: (candidate) => candidate.createdAt,
        cell: ({ row }) => dayjs(row.original.createdAt).format('D MMM YYYY'),
      },
      {
        id: 'teams',
        header: 'Teams',
        enableSorting: false,
        cell: ({ row }) => <CandidateTeamSelect candidate={row.original} />,
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <div className='flex justify-end'>
            <CandidateRowMenu candidate={row.original} />
          </div>
        ),
      },
    ],
    [],
  )

  return (
    <DataTable
      className='whitespace-nowrap'
      columns={columns}
      data={candidates}
      includeHeader={true}
      visibilityState={{ teams: isAdmin }}
      sortingState={[{ id: 'role', desc: true }]}
      emptyMessage={() => 'No candidates yet'}
      getRowId={(candidate) => candidate.characterReference.id.toString()}
    />
  )
}

export default CandidateTable
